import React, {useEffect, useState} from 'react';
import {Link as RouterLink, useHistory, useParams} from "react-router-dom";
import {useSignOut, useIsAuthenticated} from "react-auth-kit";
import {format, parse} from "date-fns";
import {requestApi, showAlertAutoDis} from "../../../utils/utils";
import {
    Container,
    FormControl,
    Grid,
    InputLabel,
    Link,
    MenuItem,
    Select,
    Typography,
    Box,
    Breadcrumbs
} from "@mui/material";
import PrimaryButton from "../../../components/Common/PrimaryButton";
import GuestBar from "../../../components/GuestInfo/GuestBar";
import useWindowSize from '../../../utils/windowSize';
import Nex from '../../../components/Locations/Nex';
import Spinner from "../../../utils/Spinner/Spinner";

const GuestAccommodationPage = () => {
    const isAuth = useIsAuthenticated();
    const {width} = useWindowSize();
    const [loader, setLoader] = useState(false);
    const [booking, setBooking] = useState({});
    const [rooms, setRooms] = useState([]);
    const [guests, setGuests] = useState([]);
    const [layoutUrl, setLayoutUrl] = useState('');
    const {id} = useParams();
    const signOut = useSignOut();
    const apiURL = document.head.querySelector('meta[name="api-url"]');
    const [values, setValues] = useState({});

    useEffect(() => {
        getAllData().catch();
    }, []);

    useEffect(async () => {
        setValues(generateInitialState(booking?.booking?.MaxDivers));
        await handleReload();
    }, [booking]);

    const handleReload = async () => {
        if (Object.keys(booking).length > 0) {
            setLoader(true);
            setLayoutUrl(`${apiURL.content}/get-layout/${booking?.booking.id}?t=${new Date().getTime()}`);
            setValues(prevState => {
                return Object.keys(prevState).reduce((acc, curr) => {
                    acc[curr] = {
                        value: '',
                        id: '',
                        single:false
                    };
                    return acc;
                }, {});
            });
            await reloadRooms();
            setLoader(false)
        }
    };

    const getAllData = async () => {
        try {
            const {data} = await requestApi(`/booking/get/${id}`);
            if (data.status === 'failed')
                return window.location = '/';
            setBooking(data);
            const {data: guestData} = await requestApi(`/bookings/getGuests/${id}`);
            if (guestData.status === 'success'){
				setGuests(guestData.guests);
				const {data: roomData, data: {data: {rooms}}} = await requestApi(`/get-booking-accommodation/${id}`);
        		if (roomData.status === 'success')
          		  setRooms(rooms);
				}

        } catch (e) {
            if (e.status === 401)
                signOut();
        }
    };

    const reloadRooms = async () => {
        const {data: roomData, data: {data: {rooms}}} = await requestApi(`/get-booking-accommodation/${id}`);
        if (roomData.status === 'success'){
			setRooms(rooms);
		}

		const {data: guestData} = await requestApi(`/bookings/getGuests/${id}`);
        if (guestData.status === 'success'){
			setGuests(guestData.guests);
		}
    }

    const handleChange = (e, guestId,single) => {
        const {name, value} = e.target;
        setValues({...values, [name]: {value: value, id: guestId, single:single}});
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        const newObject = Object.fromEntries(Object.entries(values).filter(([key, value]) => value.value !== ''));
        let guests = [];

        for (const key in newObject) {
            if (Object.hasOwnProperty.call(newObject, key)) {
                const item = newObject[key];
                const [room, bed] = item.value.split("-");
                const params = {room: room, bed: bed, id: item.id};
                guests.push(params);
            }
        }
        const {data} = await requestApi(`/save-booking-accommodation/${id}`, 'POST', {'guests': guests});

        if (data.status === 'success') {
            window.scrollTo(0, 0);
            await handleReload();
            showAlertAutoDis({title: 'Success', text: 'Guest accommodation saved successfully', manualClose: true});
        } else
            showAlertAutoDis({title: 'Error', text: data['message']});
    }

    function generateInitialState(numGuests) {
        const initialState = {};
        for (let i = 1; i <= numGuests; i++) {
            initialState[`guest${i}`] = {
                value: '',
                id: '',
                single:false
            };
        }
        return initialState;
    }

    function getGuestName(guest,index) {
        let guestName = null;
        if (guest.guestInfo.firstName && guest.guestInfo.firstName !== '' && guest.guestInfo.firstName.length > 0 && guest.guestInfo.lastName && guest.guestInfo.lastName !== '' && guest.guestInfo.lastName.length > 0)
            guestName = guest.guestInfo.firstName.substring(0, 1) + '. ' + guest.guestInfo.lastName;
        else if (guest.guestInfo.lastName && guest.guestInfo.lastName !== '' && guest.guestInfo.lastName.length > 0)
            guestName = guest.guestInfo.firstName.substring(0, 1) + '. ' + guest.guestInfo.lastName;
        return `${guest.guestInfo.sex ? guest.guestInfo.sex + ' - ' : ''}Guest ${guestName ?? index+1} ${parseInt(guest['accommodation']['isSingle']) === 1 ? 'Single Oc.' : ''}`;
    }
    function checkGenderRestrictions(roomAvailable,guest,usedBeds){
        let used= Object.keys(usedBeds).filter(b => parseInt(b) === parseInt(roomAvailable.roomNameId));
        return (roomAvailable.prefSex === "" || (["", roomAvailable.prefSex].indexOf(guest['guestInfo']['sex']) >= 0)) || (used.length>0 && parseInt(usedBeds[used][0]['bookFk'])===parseInt(guest['bookFk']))
    }

    function isGuestRoom(room, guest) {
        return parseInt(room.index) === parseInt(guest['accommodation']['roomClassFk']) && room['availBeds'].length > 0;
    }

    function isSingleOccRoom(room, guest) {
        if(parseInt(guest['accommodation']['isSingle']) === 1 ){
            if([2,3].indexOf(parseInt(guest['accommodation']['roomClassFk'])) >= 0 && (parseInt(room.index) === 3 || (parseInt(room.index) === 2 && room['availBeds'].some(({avail}) => parseInt(avail) === 2))))
                return true;
            else if(parseInt(guest['accommodation']['roomClassFk']) === parseInt(room.index) && room['availBeds'].some(({avail}) => parseInt(avail) === 2))
                return true;
        }
        return false;
    }
    function getRooms(rooms,guest){
        const Beds = [];
        rooms.filter(room => {
            return isGuestRoom(room, guest) ? true : !!isSingleOccRoom(room, guest);
        }).map(r => r['availBeds'].filter(ra => {
                return checkGenderRestrictions(ra,guest,r['usedBeds']) && parseInt(ra.roomNameId) !== parseInt(guest['accommodation']['roomNameFk']);
            }
        ).map((room) => {
            let filteredBeds = room.availBed;
            if (parseInt(guest['accommodation']['isSingle']) === 1)
                filteredBeds = room.availBed.filter(bed => parseInt(bed) === 1) || [];
            if (filteredBeds.length === 0)
                return;
            return filteredBeds.map((bed) => Beds.push({info:room,bed}))
        }))
        return Beds;
    }

    return (
        <>
            {isAuth() && Object.keys(booking).length > 0 &&
                <GuestBar>
                    <Breadcrumbs aria-label="breadcrumb" separator={'>'}>
                        <Link component={RouterLink} underline="hover" color="inherit" to="/booking/my-bookings">
                            Bookings
                        </Link>
                        <Link component={RouterLink} underline="hover" color="inherit"
                              to={`/booking/my-bookings/${booking.booking.id}`}>
                            {format(parse(booking.booking.tripDate, 'yyyy-MM-dd', new Date()), 'LLLL d, yyyy')}
                        </Link>
                        {/* <Typography color="text.primary">{form?.guestInfo?.firstName ? `${form.guestInfo.firstName} ${form.guestInfo.lastName}` : 'Guest Form'}</Typography> */}
                        <Typography color="text.primary">Select Accommodation</Typography>
                    </Breadcrumbs>
                </GuestBar>
            }
            {guests && guests.length > 0 ?
                <Container sx={{marginBottom: 5, marginTop: 5}}>
                    <Box sx={{px: {xs: '1.6rem', lg: '0'}}}>
                        <Typography variant={"h3"} sx={{marginBottom: 2}}>Select Accommodation</Typography>
                        <Typography variant={"p"} component="p">Select  your preferred state room or suite by checking the layout of the ship and choose anything marked “Available”</Typography>
                    </Box>
                    <form onSubmit={handleSubmit} style={{marginTop: '20px'}}>
                        {width < 5000 ?
                            <>
                                <Box sx={{px: {xs: '1.6rem', lg: '0'}, marginBottom: 3}}>
                                    {loader ? <div style={{width: '100%', display: 'flex', justifyContent: 'center'}}><Spinner/></div> : Object.keys(booking).length > 0 &&
                                        <img src={layoutUrl} alt={booking?.booking.trip.location.location} width={'100%'} style={{maxWidth: '1000px'}}
                                             className={'roomImage'}/>}
                                </Box>
                                <Grid container sx={{px: {xs: '1.6rem', lg: '0'}}}>
                                    <Box sx={{px: {xs: '1.6rem', lg: '0'}, marginBottom: 1}}>
                                        <Typography variant={"h4"} component={"h3"}>Your Accommodations</Typography>
                                    </Box>
                                    <Grid container spacing={3} sx={{px: {xs: '1.6rem', lg: '0'}}}>
                                        {guests.map((guest, index) => <Grid key={index} item xs={12} md={6} lg={4}>
                                            <FormControl variant="filled" fullWidth>
                                                <InputLabel id={`guest${index + 1}Label`}>
                                                    {getGuestName(guest,index)}
                                                </InputLabel>
                                                <Select
                                                    labelId={`guest${index + 1}Label`}
                                                    id={`guest${index + 1}`}
                                                    name={`guest${index + 1}`}
                                                    label={`guest${index + 1}`}
                                                    value={values[`guest${index + 1}`].value}
                                                    onChange={(e) => handleChange(e, guest['accommodation']['guestFk'],parseInt(guest['accommodation']['isSingle']) === 1)}
                                                    disableUnderline
                                                >
                                                    <MenuItem value="">{getRooms(rooms,guest).length>0?'Pick a Room to move':'No Other Room Available'}</MenuItem>
                                                    {getRooms(rooms,guest).length>0 && getRooms(rooms, guest).map((room, index) =>
                                                        <MenuItem key={index}
                                                                  value={`${room.info.roomNameId}-${room.bed}`}
                                                                  disabled={Object.values(values).some(value =>
                                                                      value.value === `${room.info.roomNameId}-${room.bed}` ||
                                                                      (value.single && parseInt(value.value.split("-")[0]) === parseInt(room.info.roomNameId)) ||
                                                                      parseInt(guest['accommodation']['isSingle']) === 1 && value.value === `${room.info.roomNameId}-2`
                                                                  )}>{room.info.roomName} -
                                                            Bed {room.bed}</MenuItem>)
                                                    }
                                                </Select>
                                            </FormControl>
                                        </Grid>)}
                                    </Grid>
                                    <Grid container>
                                        <PrimaryButton
                                            sx={{mx: {xs: '1.6rem', lg: '0'}, marginTop: '20px', width: '150px'}}
                                            type="submit">Save</PrimaryButton>
                                    </Grid>
                                </Grid>
                            </>
                            :
                            <>
                                <Box sx={{px: {xs: '1.6rem', lg: '0'}}}>
                                    <Nex booking={booking}/>
                                </Box>
                                <Grid container sx={{px: {xs: '1.6rem', lg: '0'}}}>
                                    <Grid container>
                                        <PrimaryButton
                                            sx={{mx: {xs: '1.6rem', lg: '0'}, marginTop: '20px', width: '150px'}}
                                            type="submit">Save</PrimaryButton>
                                    </Grid>
                                </Grid>
                            </>
                        }
                    </form>
                </Container> :
                <Container sx={{marginBottom: 5, marginTop: 5}}>
                    <Box sx={{px: {xs: '1.6rem', lg: '0'}}}>
                    </Box>
                </Container>
            }
        </>
    );
};

export default GuestAccommodationPage;
